import {
  BellIcon,
  CalendarIcon,
  ClockIcon,
  LinkIcon,
  MegaphoneIcon,
} from '@heroicons/react/24/outline';
import {
  ErrorMessage,
  Field,
  FieldProps,
  Form,
  Formik,
  FormikConfig,
  FormikProps,
} from 'formik';
import moment from 'moment';
import { Button, Modal } from 'react-daisyui';
import DatePicker from 'react-datepicker';
import { useTranslation } from 'react-i18next';
import { useUserContext } from '../../../../../../providers/UserProvider/User.context';
import { useWorkspaceContext } from '../../../../../../providers/WorkspaceProvider/Workspace.context';
import { tOption } from '../../../../Agenda.types';
import { allowedFieldsError, durationOptions } from '../../Calendar.constants';
import { getAllowedFields } from '../../Calendar.functions';
import { initialValues, useCalendarContext } from '../../Calendar.hook';
import { tCreateEventForm } from '../../Calendar.types';
import CustomSelect from '../../components/CustomSelect.component';
import styles from './CreateEvent.module.scss';
import { CreateEventSchema } from './CreateEvent.schema';

export default function CreateEvent() {
  const { t } = useTranslation();
  const { workspaceInfo } = useWorkspaceContext();
  const { currentUser } = useUserContext();
  const {
    showCreateEvent,
    setShowCreateEvent,
    onCreateEventSubmit,
    templates,
    selectedEvent,
    setSelectedEvent,
  } = useCalendarContext();

  const now = new Date();
  const minTime = new Date();

  minTime.setHours(7);
  minTime.setMinutes(0);

  const workspaceUsersOptions: tOption[] =
    Object.values(workspaceInfo?.users ?? {}).map((user) => ({
      value: user.authId,
      label: user.name,
    })) || [];
  const formikConfig: FormikConfig<tCreateEventForm> = {
    initialValues: {
      ...initialValues,
      startDate: now,
      time: now,
      duration: '00:15:00',
      guests: [
        {
          value: currentUser?.authId || '',
          label: currentUser?.name || '',
        },
      ],
    },
    onSubmit: onCreateEventSubmit,
    validateOnChange: true,
    validateOnBlur: true,
    validationSchema: CreateEventSchema,
  };
  const defaultOptions: tOption[] = [
    {
      value: currentUser?.authId || '',
      label: currentUser?.name || '',
    },
  ];

  return (
    <Modal.Legacy
      open={showCreateEvent}
      onClickBackdrop={() => {
        setSelectedEvent(undefined);
        setShowCreateEvent(false);
      }}
      className="bg-base-100 max-w-screen-sm no-scrollbar p-0 rounded-md"
    >
      <Formik {...formikConfig}>
        {(formikProps: FormikProps<tCreateEventForm>) => {
          const { setFieldValue, resetForm, values, touched, isValid } =
            formikProps;
          const allowedFields = getAllowedFields(templates, values);

          const handleOnClose = () => {
            setSelectedEvent(undefined);
            setShowCreateEvent(false);
            resetForm();
          };

          const isDisabledButton = (() => {
            if (!isValid) return true;
            if (!touched.title) return true;
            if (!!allowedFields.description && !values.description) return true;
            if (!!allowedFields.address && !values.address) return true;
            if (!!allowedFields.link && !values.link) return true;
            if (!!allowedFields.notes && !values.note) return true;
            if (!values.guests) return true;
            return false;
          })();

          const isValidDescription = touched.description && !values.description;
          const isValidAddress = touched.address && !values.address;
          const isValidLink = touched.link && !values.link;
          const isValidNote = touched.note && !values.note;
          const isValidGuests = touched.guests && !values.guests;

          return (
            <Form className={styles.CreateEvent}>
              <h1 className={styles.CreateEventTitle}>
                {t('agenda/agenda:create-new-event')}
              </h1>
              <div className={styles.InputWrapper}>
                <label htmlFor="templateId" className={styles.Label}>
                  {t('agenda/agenda:what-event-type')}
                </label>
                <div className={styles.InputBox}>
                  <BellIcon className="w-5 h-5" />
                  <Field name="templateId" id="templateId">
                    {({ field }: FieldProps<string>) => (
                      <select {...field} className={styles.Select}>
                        <option value="" className="flex">
                          {t('agenda/agenda:select-a-template')}
                        </option>
                        {templates?.map((template) => (
                          <option value={template.id} key={template.id}>
                            {String.fromCodePoint(parseInt(template.emoji, 16))}{' '}
                            {template.name}
                          </option>
                        ))}
                      </select>
                    )}
                  </Field>
                </div>

                <ErrorMessage
                  name="templateId"
                  component="div"
                  className={styles.ErrorMessage}
                />
              </div>

              <div className={styles.InputWrapper}>
                <label htmlFor="title" className={styles.Label}>
                  {t('agenda/agenda:add-a-title')}
                </label>
                <div className={styles.InputBox}>
                  <MegaphoneIcon className="w-5 h-5" />
                  <Field
                    type="text"
                    className={styles.Input}
                    placeholder="Ingresa el título del evento"
                    name="title"
                  />
                </div>

                <ErrorMessage
                  name="title"
                  component="div"
                  className={styles.ErrorMessage}
                />
              </div>

              <div className={styles.InputWrapperFlex}>
                <div className="w-1/2">
                  <div>
                    <label htmlFor="startDate" className={styles.Label}>
                      {t('agenda/agenda:select-a-date')}
                    </label>

                    <div className={styles.InputBox}>
                      <CalendarIcon className="w-6" />
                      <Field className={styles.Input} name="startDate">
                        {({ field }: FieldProps) => {
                          const selectedDate = selectedEvent?.start
                            ? moment(selectedEvent.start).toDate()
                            : moment(field.value).toDate();

                          return (
                            <DatePicker
                              className={styles.Input}
                              selected={selectedDate}
                              popperClassName="ReactDatepicker_Popper"
                              wrapperClassName="ReactDatepicker_Wrapper"
                              onChange={(val) => setFieldValue(field.name, val)}
                              minDate={new Date()}
                            />
                          );
                        }}
                      </Field>
                    </div>
                    <ErrorMessage
                      name="startDate"
                      component="div"
                      className={styles.ErrorMessage}
                    />
                  </div>
                </div>

                <div className="w-1/2">
                  <div>
                    <label htmlFor="time" className={styles.Label}>
                      {t('agenda/agenda:select-an-hour')}
                    </label>

                    <div className={styles.InputBox}>
                      <ClockIcon className="w-6" />
                      <Field name="time">
                        {({ field }: FieldProps) => {
                          const selectedDate = selectedEvent?.start
                            ? moment(selectedEvent.start).toDate()
                            : moment(field.value).toDate();

                          return (
                            <DatePicker
                              className={styles.Input}
                              selected={selectedDate}
                              popperClassName="ReactDatepicker_Popper"
                              wrapperClassName="ReactDatepicker_Wrapper"
                              showTimeSelect
                              showTimeSelectOnly
                              timeIntervals={15}
                              timeCaption="Time"
                              dateFormat="h:mm aa"
                              onChange={(val) => setFieldValue(field.name, val)}
                              minDate={new Date()}
                            />
                          );
                        }}
                      </Field>
                    </div>

                    <ErrorMessage
                      name="time"
                      component="div"
                      className={styles.ErrorMessage}
                    />
                  </div>
                </div>
              </div>

              {!!allowedFields.description && (
                <div className={styles.InputWrapper}>
                  <label htmlFor="description" className={styles.Label}>
                    {t('agenda/agenda:write-a-description')}
                  </label>
                  <div className={styles.InputBox}>
                    <img
                      src="/images/agenda/ChatBubbleIcon.svg"
                      alt="bell icon"
                      className="w-6"
                    />
                    <Field
                      type="text"
                      name="description"
                      className={styles.Input}
                      placeholder="Ej. Llamar al cliente para recordar de las promociones"
                    />
                  </div>

                  {isValidDescription && (
                    <p className={styles.ErrorMessage}>{allowedFieldsError}</p>
                  )}
                  <ErrorMessage
                    name="description"
                    component="div"
                    className={styles.ErrorMessage}
                  />
                </div>
              )}

              <div className={styles.InputWrapper}>
                <label htmlFor="duration" className={styles.Label}>
                  {t('agenda/agenda:event-duration')}
                </label>

                <div className={styles.InputBox}>
                  <ClockIcon className="w-6" />
                  <Field className={styles.Input} name="duration" id="duration">
                    {({ field }: FieldProps<string>) => (
                      <select {...field} className={styles.Select}>
                        {durationOptions.map((option) => (
                          <option value={option.value} key={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    )}
                  </Field>
                </div>

                <ErrorMessage
                  name="duration"
                  component="div"
                  className={styles.ErrorMessage}
                />
              </div>

              {!!allowedFields.address && (
                <div className={styles.InputWrapper}>
                  <label htmlFor="address" className={styles.Label}>
                    {t('agenda/agenda:add-address')}
                  </label>

                  <div className={styles.InputBox}>
                    <img
                      src="/images/agenda/MapPinIcon.svg"
                      alt="bell icon"
                      className="w-6"
                    />
                    <Field
                      type="text"
                      name="address"
                      className={styles.Input}
                      placeholder="Ej. Monte Alban 304, Int. 5"
                    />
                  </div>

                  {isValidAddress && (
                    <p className={styles.ErrorMessage}>{allowedFieldsError}</p>
                  )}
                  <ErrorMessage
                    name="address"
                    component="div"
                    className={styles.ErrorMessage}
                  />
                </div>
              )}

              {!!allowedFields.links && (
                <div className={styles.InputWrapper}>
                  <label htmlFor="link" className={styles.Label}>
                    {t('agenda/agenda:add-link')}
                  </label>
                  <div className={styles.InputBox}>
                    <LinkIcon className="w-5" />
                    <Field
                      type="text"
                      name="link"
                      className={styles.Input}
                      placeholder="Ej. http://www.ejemplo.com"
                    />
                  </div>

                  {isValidLink && (
                    <p className={styles.ErrorMessage}>{allowedFieldsError}</p>
                  )}
                  <ErrorMessage
                    name="link"
                    component="div"
                    className={styles.ErrorMessage}
                  />
                </div>
              )}

              {!!allowedFields.users && (
                <div className={styles.InputWrapper}>
                  <label htmlFor="guests" className={styles.Label}>
                    {t('agenda/agenda:want-to-invite')}
                  </label>

                  <Field
                    name="guests"
                    component={CustomSelect}
                    options={workspaceUsersOptions}
                    placeholder="Busca al contacto que quieres invitar"
                    isMulti
                    defaultOptions={defaultOptions}
                  />

                  {isValidGuests && (
                    <p className={styles.ErrorMessage}>{allowedFieldsError}</p>
                  )}
                  <ErrorMessage
                    name="guests"
                    component="div"
                    className={styles.ErrorMessage}
                  />
                </div>
              )}

              {!!allowedFields.notes && (
                <div className={styles.InputWrapper}>
                  <label htmlFor="note" className={styles.Label}>
                    {t('agenda/agenda:want-to-add-a-note')}
                  </label>
                  <div className={styles.InputBox}>
                    <img
                      src="/images/agenda/ChatBubbleIcon.svg"
                      alt="bell icon"
                      className="w-6"
                    />
                    <Field
                      type="text"
                      name="note"
                      className={styles.Input}
                      placeholder="Ej. Mencionar las futuras promociones."
                    />
                  </div>
                  {isValidNote && (
                    <p className={styles.ErrorMessage}>{allowedFieldsError}</p>
                  )}
                  <ErrorMessage
                    name="note"
                    component="div"
                    className={styles.ErrorMessage}
                  />
                </div>
              )}

              <div className={styles.FormOptions}>
                <Button
                  color="ghost"
                  variant="outline"
                  type="button"
                  onClick={() => handleOnClose()}
                >
                  {t('agenda/agenda:cancel-button')}
                </Button>

                <Button
                  color="primary"
                  type="submit"
                  disabled={isDisabledButton}
                >
                  {t('agenda/agenda:save-button')}
                </Button>
              </div>
            </Form>
          );
        }}
      </Formik>
    </Modal.Legacy>
  );
}
